import { getApiClient } from './authApiClient';

// Base URL for the company service
// const COMPANY_SERVICE_URL = 'https://company-service.dev.crediarc.cloudops.space/api';
// const COMPANY_SERVICE_URL = 'http://localhost:3001/api';

// All functions now accept apiClient as the first argument

export const checkToken = async () => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get('/check-token');
  return response.data;
};

export const getAllCompanies = async () => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get('/companies/fetchall');
  return response.data;
};

export const createCompany = async (companyData) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post('/companies', companyData);
  return response.data;
};

export const createCompanyFromPlatform = async (companyData) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post('/companies/platform', companyData);
  return response.data;
};

export const createCompanyWithEnrichment = async (companyData) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post('/companies/create-enriched', companyData);
  return response.data;
};

export const getCompanyById = async (id) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/companies/${id}`);
  return response.data;
};

// Fetch companies for a tenant with pagination (segments list)
export const getCompaniesForTenantSegments = async (tenantId, limit = 10, nextToken = null) => {
  const apiClient = getApiClient('company');
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (nextToken) params.append('nextToken', nextToken);

    const response = await apiClient.get(`/companies/tenant/${tenantId}/segments?${params.toString()}`);
    return response.data; // should contain { items: [...], nextToken: '...' }
  } catch (error) {
    console.error('Error fetching companies for tenant segments:', error);
    throw error;
  }
};

// Update a company
export const updateCompany = async (id, companyData) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.put(`/companies/${id}`, companyData);
    return response.data;
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

// Delete a company
export const deleteCompany = async (id) => {
  const apiClient = getApiClient('company');
  try {
    // Change from /companies/delete/${id} to /companies/${id}
    const response = await apiClient.delete(`/companies/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting company:', error);
    throw error;
  }
};

// Search companies
export const searchCompanies = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/tenant/${tenantId}/search`);
    return response.data;
  } catch (error) {
    console.error('Error searching companies:', error);
    throw error;
  }
};

// Search companies by segment ID
export const getCompaniesBySegmentId = async (tenantId, segmentId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(
      `/companies/tenant/${tenantId}/search?segmentId=${segmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error searching companies by segment ID:', error);
    throw error;
  }
};


// Search parent companies
export const searchParentCompanies = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/search/parent/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error('Error searching parent companies:', error);
    throw error;
  }
};

// Get all companies with pagination
export const getAllCompaniesPaginated = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/paginated/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated companies:', error);
    throw error;
  }
};

// Get company by name
export const getCompanyByName = async (tenantId, companyName) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/tenant/${tenantId}/name/${encodeURIComponent(companyName)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching company by name:', error);
    throw error;
  }
};

// Search companies with advanced filters
export const searchCompaniesAdvanced = async (tenantId, filters) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/tenant/${tenantId}/search/advanced`, {
      params: filters
    });
    return response.data;
  } catch (error) {
    console.error('Error searching companies with advanced filters:', error);
    throw error;
  }
};

// Get company data by account ID or name
export const getCompanyData = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/data/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching company data:', error);
    throw error;
  }
};

// Company Entity Registry endpoints
export const getCompanyEntityRegistry = async (tenantId, companyId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/company-entity-registry/${tenantId}/${companyId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching company entity registry:', error);
    throw error;
  }
};

export const createCompanyEntityRegistry = async (registryData) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/company-entity-registry/', registryData);
    return response.data;
  } catch (error) {
    console.error('Error creating company entity registry:', error);
    throw error;
  }
};

// Tenant endpoints
export const getTenant = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/companies/tenant/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tenant:', error);
    throw error;
  }
};

// Test Firestore connection
export const testFirestoreConnection = async () => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get('/test-firestore');
    return response.data;
  } catch (error) {
    console.error('Error testing Firestore connection:', error);
    throw error;
  }
};

// DnB Search endpoints
export const searchCompanyDnB = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { name, tenantId, iso2Code, countryName } = searchParams;
    const response = await apiClient.get('/dnb/companies/search', {
      params: { name, tenantId, iso2Code, countryName }
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error searching company in DnB:', error);
    throw error;
  }
};

export const searchCompanyDnBWithRegistration = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { tenantId, registrationNumber, iso2Code } = searchParams;
    const response = await apiClient.get(`/dnb/companies/search/registration/${registrationNumber}`, {
      params: { iso2Code, tenantId }
    });
    return response.data;
  } catch (error) {
     if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error searching company with registration in DnB:', error);
    throw error;
  }
};

export const searchCompanyDnBWithTicker = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { tenantId, ticker, iso2Code } = searchParams;
    const response = await apiClient.get(`/dnb/companies/search/ticker/${ticker}`, {
      params: { iso2Code, tenantId }
    });
    return response.data;
  } catch (error) {
     if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error searching company with ticker in DnB:', error);
    throw error;
  }
};

// OpenAI Search endpoint
export const searchCompanyOpenAI = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { searchText, searchCountry, tenantId } = searchParams;
    const response = await apiClient.get('/openai/companies/search', {
      params: { searchText, searchCountry, tenantId }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching company in OpenAI:', error);
    throw error;
  }
};

// Perplexity Search endpoint
export const searchCompanyPerplexity = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { searchText, countryName } = searchParams;
    const response = await apiClient.get('/perplexity/companies/search', {
      params: { searchText, countryName }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching company in Perplexity:', error);
    throw error;
  }
};

export const getCompanyDnBInfo = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
    const { dnbCompanyId, tenantId} = searchParams;
    const response = await apiClient.get(`/dnb/companies/${dnbCompanyId}`, { params: { tenantId } });
    return response.data;
  } catch (error) {
     if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error getting company info from DnB:', error);
    throw error;
  }
};

export const getCompanyDnBFinancials = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
        const { dnbCompanyId, tenantId} = searchParams;
    const response = await apiClient.get(`/dnb/companies/${dnbCompanyId}/financials`, { params: { tenantId } });
    return response.data;
  } catch (error) {
     if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error getting company financials from DnB:', error);
    throw error;
  }
};

export const getCompanyDnBFinancialsStrength = async (searchParams) => {
  const apiClient = getApiClient('company');
  try {
        const { dnbCompanyId, tenantId} = searchParams;
    const response = await apiClient.get(`/dnb/companies/${dnbCompanyId}/financials/strength`, { params: { tenantId } });
    return response.data;
  } catch (error) {
     if (error.response?.data?.message === 'DNB Search is not enabled for this tenant') {
            return error;
        }
    console.error('Error getting company financials strength from DnB:', error);
    throw error;
  }
};

export const getBulkUploadSignedUrl = async (tenantId, filename, signal) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/companies/bulk-upload-signed-url', { tenantId, filename }, { signal });
    return response.data;
  } catch (error) {
    console.error('Error getting bulk upload signed URL:', error);
    throw error;
  }
};

export const processBulkUpload = async (tenantId, filename, signal) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/companies/process-bulk-upload', { tenantId, filename }, { signal });
    return response.data;
  } catch (error) {
    console.error('Error processing bulk upload:', error);
    throw error;
  }
};

export const getBulkUploadProgress = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/companies/bulk-upload-progress/${tenantId}`);
  return response.data?.data || null;
};

export const cancelBulkUpload = async (tenantId, jobId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/companies/bulk-upload-progress/${tenantId}/${jobId}/cancel`);
  return response.data?.data || null;
};

export const findRelatedCompanies = async (searchParams) => {
    const apiClient = getApiClient('company');
    try {
        const { companyName, dnbCompanyId, tenantId } = searchParams;
        
        const params = new URLSearchParams({
            companyName: companyName,
            dnbCompanyId: dnbCompanyId,
            tenantId: tenantId,
        });
        
        const response = await apiClient.get(`/dnb/companies/related?${params}`);

        return response.data;
    } catch (error) {
        console.error('Error in findRelatedCompanies:', error);
        throw error;
    }
};

// Get signed URL for direct upload to Google Cloud Storage
export const getFinancialFileUploadSignedUrl = async (companyId, { fileName, fileType, tenantId }) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/chat-files/companies/${companyId}/files/upload-url`, {
      fileName,
      fileType,
      tenantId
    });
    return response.data;
  } catch (error) {
    console.error('Error getting file upload signed URL:', error);
    throw error;
  }
};

// Get signed URL for additional files upload
export const getAdditionalFileUploadSignedUrl = async (companyId, { fileName, fileType, tenantId }) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/chat-files/companies/${companyId}/files/additional/upload-url`, {
      fileName,
      fileType,
      tenantId
    });
    return response.data;
  } catch (error) {
    console.error('Error getting additional file upload signed URL:', error);
    throw error;
  }
};

export const createFileRecord = async (companyId, fileData) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/chat-files/companies/${companyId}/files`, fileData);
    return response.data;
  } catch (error) {
    console.error('Error creating file record:', error);
    throw error;
  }
};

export const getFinancialFiles = async (companyId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/chat-files/companies/${companyId}/files`);
    return response.data;
  } catch (error) {
    console.error('Error getting company files:', error);
    throw error;
  }
};

export const updateFile = async (fileId, updateData) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.put(`/chat-files/files/${fileId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating file:', error);
    throw error;
  }
};

export const deleteFile = async (fileId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.delete(`/chat-files/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

export const getFileDownloadUrl = async (fileId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/chat-files/files/${fileId}/download-url`);
    return response.data;
  } catch (error) {
    console.error('Error getting file download URL:', error);
    throw error;
  }
};


export const processFile = async (fileId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/chat-files/process-file/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error triggering file processing:', error);
    throw error;
  }
};

export const getAdditionalFileDownloadUrl = async (fileId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/chat-files/files/${fileId}/additional/download-url`);
    return response.data;
  } catch (error) {
    console.error('Error getting additional file download URL:', error);
    throw error;
  }
};

// Get signed URL for file upload (generic method)
export const getAgingUploadSignedUrl = async ({ fileName, fileType, tenantId }) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/aging/aging-upload-signed-url`, {
      fileName,
      fileType,
      tenantId
    });
    return response.data;
  } catch (error) {
    console.error('Error getting signed url:', error);
    throw error;
  }
};

// AR+ AI-assisted extraction: propose a canonical column mapping for a raw aging file. Pass a
// `grid` (first ~20 rows incl. any preamble) for full header/row detection, or headers+sampleRows.
export const suggestAgingMapping = async (headers, sampleRows, grid) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/suggest-mapping`, { headers, sampleRows, grid }, { timeout: 90000 });
  return response.data?.data || { mappings: {}, warnings: [], headerRowIndex: 0, dropRowLabels: [] };
};

// Prospect import: infer CrediArc company fields from arbitrary spreadsheet headers and samples.
export const suggestProspectMapping = async (headers, sampleRows) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/suggest-prospect-mapping`, { headers, sampleRows }, { timeout: 90000 });
  return response.data?.data || { mappings: {}, warnings: [], engine: null };
};

// AR+ invoice-level analyzer: pick the invoice ledger sheet in a workbook + map to invoice schema.
export const analyzeInvoiceSheets = async (sheets) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/analyze-sheets`, { sheets }, { timeout: 90000 });
  return response.data?.data || { sheetIndex: 0, mappings: {}, headerRowIndex: 0, dropRowLabels: [] };
};

// AR+ format-agnostic analysis of a whole upload (all sheets across all files), classified by role.
// `guidance` carries the user's corrections from the review chat so the re-analysis honors them.
export const analyzeWorkbook = async (files, guidance = '') => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/analyze-workbook`, { files, guidance }, { timeout: 120000 });
  return response.data?.data || { sheets: [], summary: '', warnings: [] };
};

// AR+ clarification chatbot: converse with AIArc to resolve file ambiguities before import.
export const clarifyAgingMapping = async (grid, messages, schema) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/clarify`, { grid, messages, schema }, { timeout: 90000 });
  return response.data?.data || { reply: '', ready: false, mappings: null };
};

// aging process endpoint
export const processAgingFiles = async (tenantId, agingKey, paymentKey, latestAgingDate, arThreshold, importId, replaceExisting = true, ageByStatement = true) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(`/aging/process`, {
      tenantId,
      agingKey,
      paymentKey,
      latestAgingDate,
      arThreshold,
      importId,
      replaceExisting,
      ageByStatement
    });
    return response.data;
  } catch (error) {
    console.error('Error processing aging files:', error);
    throw error;
  }
};

// AR+ stop & roll back an in-flight aging import.
export const cancelAgingImport = async (importId, tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/aging/cancel`, { importId, tenantId }, { timeout: 120000 });
  return response.data?.data || { recordsDeleted: 0, companiesDeleted: 0 };
};

// AR+ poll real backend progress for an in-flight import (lines stored, customers created, errors).
export const getAgingImportStatus = async (importId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/aging/import/${importId}`);
  return response.data?.data || { status: 'unknown' };
};

// AR+ live scoring progress for an import (how many companies finished underwriting + which).
export const getAgingScoringProgress = async (importId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/aging/import/${importId}/scoring`);
  return response.data?.data || { total: 0, scored: 0, pending: 0, inProgressNames: [], doneNames: [] };
};

// AR+ latest import for a tenant — powers the on-page import log on the AR dashboard.
export const getLatestAgingImport = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/aging/latest-import/${tenantId}`);
  return response.data?.data || null;
};

// Tenant-level company file uploads (signed URL -> direct to GCS)
export const getCompanyUploadedFileSignedUrl = async (tenantId, { fileName, fileType }) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(
      `/company-uploaded-files/tenants/${tenantId}/upload-url`,
      { fileName, fileType }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting company uploaded file signed URL:', error);
    throw error;
  }
};

export const listCompanyUploadedFiles = async (tenantId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.get(`/company-uploaded-files/tenants/${tenantId}`);
    return response.data;
  } catch (error) {
    console.error('Error listing company uploaded files:', error);
    throw error;
  }
};

export const getCompanyUploadedFileDownloadUrl = async (tenantId, fileGCSKey) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post(
      `/company-uploaded-files/tenants/${tenantId}/download-url`,
      { fileGCSKey }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting company uploaded file download URL:', error);
    throw error;
  }
};

export const deleteCompanyUploadedFile = async (tenantId, fileGCSKey) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.delete(
      `/company-uploaded-files/tenants/${tenantId}`,
      { data: { fileGCSKey } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting company uploaded file:', error);
    throw error;
  }
};

export const getPaymentPlatformOverview = async (tenantId) => {
  const apiClient = getApiClient('company');
  let lastError;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/overview`);
      return response.data?.data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const transient = !status || [408, 429, 500, 502, 503, 504].includes(status);
      if (!transient || attempt === 2) throw error;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError;
};

export const updatePaymentPlatformModel = async (tenantId, model) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.put(`/payment-platform/tenants/${tenantId}/model`, model);
  return response.data?.data;
};

export const simulatePaymentPlatformModel = async (tenantId, payload) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/model/simulate`, payload);
  return response.data?.data;
};

export const backtestPaymentPlatformModel = async (tenantId, model) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/model/backtest`, model);
  return response.data?.data;
};

export const getPaymentPlatformModelVersions = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/model/versions`);
  return response.data?.data || [];
};

export const getPaymentPlatformMLWorkspace = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/ml`);
  return response.data?.data;
};

export const trainPaymentPlatformMLModel = async (tenantId, type) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/ml/train`, { type });
  return response.data?.data;
};

export const runPaymentPlatformModelPipeline = async (tenantId, pipeline) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/ml/pipeline/run`, pipeline);
  return response.data?.data;
};

export const deployPaymentPlatformMLModel = async (tenantId, experimentId, deployment) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/ml/experiments/${experimentId}/deploy`, deployment);
  return response.data?.data;
};

export const getPaymentPlatformBucket = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/bucket`);
  return response.data?.data;
};

export const savePaymentPlatformBucket = async (tenantId, mapping) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.put(`/payment-platform/tenants/${tenantId}/bucket`, mapping);
  return response.data?.data;
};

export const getPaymentPlatformBucketOutputUploadUrl = async (tenantId, file) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/bucket/output-upload-url`,
    file,
  );
  return response.data?.data;
};

export const confirmPaymentPlatformBucketOutputUpload = async (tenantId, objectName) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/bucket/output-upload-complete`,
    { objectName },
  );
  return response.data?.data;
};

export const uploadPaymentPlatformBucketOutput = async (tenantId, file) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/bucket/output-upload`,
    file,
    {
      params: {
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
      },
      headers: { "Content-Type": "application/octet-stream" },
    },
  );
  return response.data?.data;
};

export const syncPaymentPlatformBucket = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/bucket/sync`);
  return response.data?.data;
};

export const refreshAllPaymentPlatformData = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/refresh-all`);
  return response.data?.data;
};

export const getPaymentPlatformFiles = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/files`);
  return response.data?.data || { items: [], count: 0 };
};

export const registerPaymentPlatformFile = async (tenantId, file) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/files`, file);
  return response.data?.data;
};

export const updatePaymentPlatformFileLabels = async (tenantId, fileId, labels) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.patch(
    `/payment-platform/tenants/${tenantId}/files/${fileId}`,
    { labels },
  );
  return response.data?.data;
};

export const deletePaymentPlatformFile = async (tenantId, fileId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.delete(`/payment-platform/tenants/${tenantId}/files/${fileId}`);
  return response.data?.data;
};

export const previewAllPaymentPlatformFiles = async (tenantId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/files/preview`);
  return response.data?.data;
};

export const previewPaymentPlatformImport = async (tenantId, files) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/imports/preview`, { files });
  return response.data?.data;
};

export const startPaymentPlatformImport = async (tenantId, files, labelConfig = null, columnMapping = null) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/imports`, {
    files,
    labelConfig,
    columnMapping,
  });
  return response.data?.data;
};

export const cancelPaymentPlatformImport = async (tenantId, jobId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/imports/${jobId}/cancel`);
  return response.data?.data;
};

export const reprocessPaymentPlatformImport = async (tenantId, jobId, columnMapping) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/imports/${jobId}/reprocess`, columnMapping);
  return response.data?.data;
};

export const savePaymentPlatformColumnMapping = async (tenantId, jobId, columnMapping) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.put(`/payment-platform/tenants/${tenantId}/imports/${jobId}/mapping`, columnMapping);
  return response.data?.data;
};

export const getPaymentPlatformImport = async (tenantId, jobId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/imports/${jobId}`);
  return response.data?.data;
};

export const createPaymentPlatformAssessment = async (tenantId, payload) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/assessments`, payload);
  return response.data?.data;
};

export const analyzePaymentPlatformBankStatement = async (tenantId, file, companyName = '') => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/bank-statements/analyze`,
    file,
    {
      params: { fileName: file.name, companyName },
      headers: { 'Content-Type': 'application/pdf' },
      timeout: 30000,
    },
  );
  let statement = response.data?.data;
  for (let attempt = 0; attempt < 120; attempt += 1) {
    if (statement?.status === 'analyzed') return statement;
    if (statement?.status === 'error') {
      const error = new Error(statement.errorMessage || 'The bank statement could not be analyzed.');
      error.response = { data: { message: error.message } };
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const status = await apiClient.get(
      `/payment-platform/tenants/${tenantId}/bank-statements/${statement.id}`,
    );
    statement = status.data?.data;
  }
  const error = new Error('Bank statement analysis timed out. Please try again.');
  error.response = { data: { message: error.message } };
  throw error;
};

export const searchPaymentPlatformEntities = async (tenantId, params = {}) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/entities`, { params });
  return response.data?.data;
};

export const exportPaymentPlatformEntities = async (tenantId, params = {}) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/entities-export`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const getPaymentPlatformEntity = async (tenantId, entityId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/entities/${entityId}`);
  return response.data?.data;
};

export const getPaymentPlatformEntityFinancials = async (tenantId, entityId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/entities/${entityId}/financials`);
  return response.data?.data;
};

export const autofillPaymentPlatformAssessment = async (tenantId, source, fileName = 'pasted-company-data.txt') => {
  const apiClient = getApiClient('company');
  const body = source instanceof Blob ? source : new Blob([String(source || '')], { type: 'application/octet-stream' });
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/assessments/autofill`,
    body,
    {
      params: { fileName },
      headers: { 'Content-Type': 'application/octet-stream' },
    },
  );
  return response.data?.data;
};

export const deletePaymentPlatformEntity = async (tenantId, entityId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.delete(`/payment-platform/tenants/${tenantId}/entities/${entityId}`);
  return response.data?.data;
};

export const startPaymentPlatformFullUnderwriting = async (tenantId, entityId, options = {}) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(
    `/payment-platform/tenants/${tenantId}/entities/${entityId}/full-underwriting`,
    { forceFreshUcc: options.forceFreshUcc !== false },
  );
  return response.data?.data;
};

export const getPaymentPlatformFullUnderwritingJob = async (tenantId, jobId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/full-underwriting/${jobId}`);
  return response.data?.data;
};

export const reviewPaymentPlatformEntity = async (tenantId, entityId, payload) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/entities/${entityId}/manual-review`, payload);
  return response.data?.data;
};

export const setPaymentPlatformEntityArchived = async (tenantId, entityId, archived = true) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.put(`/payment-platform/tenants/${tenantId}/entities/${entityId}/archive`, { archived });
  return response.data?.data;
};

export const getPaymentPlatformOfferLift = async (tenantId, entityId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.get(`/payment-platform/tenants/${tenantId}/entities/${entityId}/offerlift`);
  return response.data?.data;
};

export const startPaymentPlatformOfferLift = async (tenantId, entityId) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/entities/${entityId}/offerlift`);
  return response.data?.data;
};

export const createPaymentPlatformOfferLiftInvitation = async (tenantId, entityId, options = {}) => {
  const apiClient = getApiClient('company');
  const response = await apiClient.post(`/payment-platform/tenants/${tenantId}/entities/${entityId}/offerlift/invitations`, options);
  return response.data?.data;
};

// Replace the existing startUnderwritingFlow function
export const startUnderwritingFlow = async (tenantId, companyId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/companies/start-underwriting', {
      tenantId,
      companyId
    });
    return response.data;
  } catch (error) {
    console.error('Error starting underwriting flow:', error);
    throw error;
  }
};

export const mockUnderwritingFlow = async (tenantId, companyId) => {
  const apiClient = getApiClient('company');
  try {
    const response = await apiClient.post('/companies/mock-underwriting', {
      tenantId,
      companyId
    });
    return response.data;
  } catch (error) { 
    console.error('Error starting mock underwriting flow:', error);
    throw error;
  }
};

// Default export
const companyApi = {
  checkToken,
  getAllCompanies,
  getCompaniesForTenantSegments,
  getCompaniesBySegmentId,
  createCompany,
  createCompanyWithEnrichment,
  getCompanyById,
  updateCompany,
  deleteCompany,
  searchCompanies,
  searchParentCompanies,
  getAllCompaniesPaginated,
  getCompanyByName,
  searchCompaniesAdvanced,
  getCompanyData,
  getCompanyEntityRegistry,
  createCompanyEntityRegistry,
  getTenant,
  testFirestoreConnection,
  // DnB functions
  searchCompanyDnB,
  searchCompanyDnBWithRegistration,
  searchCompanyDnBWithTicker,
  getCompanyDnBInfo,
  getCompanyDnBFinancials,
  getCompanyDnBFinancialsStrength,
  // OpenAI and Perplexity functions
  searchCompanyOpenAI,
  searchCompanyPerplexity,
  getBulkUploadSignedUrl,
  processBulkUpload,
  getBulkUploadProgress,
  cancelBulkUpload,
  findRelatedCompanies,
  getFinancialFileUploadSignedUrl,
  getAdditionalFileUploadSignedUrl,
  createFileRecord,
  getFinancialFiles,
  updateFile,
  deleteFile,
  getFileDownloadUrl,
  getAdditionalFileDownloadUrl,
  startUnderwritingFlow,
  mockUnderwritingFlow,
  // Aging Function
  getAgingUploadSignedUrl,
  processAgingFiles,
  getAgingImportStatus,
  getAgingScoringProgress,
  getLatestAgingImport,
  processFile,
  // Tenant company uploaded files
  getCompanyUploadedFileSignedUrl,
  listCompanyUploadedFiles,
  getCompanyUploadedFileDownloadUrl,
  deleteCompanyUploadedFile,
  getPaymentPlatformOverview,
  updatePaymentPlatformModel,
  simulatePaymentPlatformModel,
  backtestPaymentPlatformModel,
  getPaymentPlatformModelVersions,
  getPaymentPlatformMLWorkspace,
  trainPaymentPlatformMLModel,
  runPaymentPlatformModelPipeline,
  deployPaymentPlatformMLModel,
  getPaymentPlatformBucket,
  savePaymentPlatformBucket,
  getPaymentPlatformBucketOutputUploadUrl,
  confirmPaymentPlatformBucketOutputUpload,
  uploadPaymentPlatformBucketOutput,
  syncPaymentPlatformBucket,
  getPaymentPlatformFiles,
  registerPaymentPlatformFile,
  updatePaymentPlatformFileLabels,
  deletePaymentPlatformFile,
  previewAllPaymentPlatformFiles,
  previewPaymentPlatformImport,
  startPaymentPlatformImport,
  cancelPaymentPlatformImport,
  reprocessPaymentPlatformImport,
  savePaymentPlatformColumnMapping,
  getPaymentPlatformImport,
  createPaymentPlatformAssessment,
  analyzePaymentPlatformBankStatement,
  searchPaymentPlatformEntities,
  exportPaymentPlatformEntities,
  getPaymentPlatformEntity,
  deletePaymentPlatformEntity,
  startPaymentPlatformFullUnderwriting,
  getPaymentPlatformFullUnderwritingJob,
  setPaymentPlatformEntityArchived,
  getPaymentPlatformOfferLift,
  startPaymentPlatformOfferLift,
  createPaymentPlatformOfferLiftInvitation,
};

export default companyApi;
